/**
 * @file ContractProductGroupPicker.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-29
 * @modified 2026-10-29
 * @description "Add a product group" in a contract window (Cam, 28 Oct 2026 —
 *              O1: product groups "can then inform contract pricing"; on what a
 *              contract for a group covers: "Plain product only, same pack").
 *              Choose a group and every plain member of the contract's
 *              category and pack goes on the contract as a product row, each
 *              with its own item cost and supplier, the price typed once for
 *              all (each product's price can be changed after). Members on the
 *              contract already are left; additive versions, archived items,
 *              other categories and other packs are listed as left out with
 *              why. The rules are utils/contractProductGroups.js (the server's
 *              copy decides the same).
 *
 *              Greyed with the reason before migrations/contract_product_groups.sql
 *              has run; "No product groups yet" while there are none (as on
 *              production until the reviewed workbook is applied).
 *
 * Props:
 *   info        GET /api/pricing/contract-product-groups — { ready, groups_ready,
 *               reason, total, other, groups } (null while loading)
 *   categoryId  the contract's category
 *   onContract  item ids on the contract now (every line)
 *   disabled    view mode
 *   request     { groupId, nonce } — open with this group chosen (the window's
 *               "Add n new products from the group")
 *   defaultPrice the price the contract's products share — the price box
 *               starts at it when opened by `request`
 *   onAdd       ({ group, members, price, pack }) => void
 */

import React, { useEffect, useMemo, useState } from 'react';
import SearchableDropdown from './SearchableDropdown';
import Input from './Input';
import { useSlideOutButton } from './SlideOutButton';
import { formatPrice } from '../utils/currencyFormatter';
import {
  groupAddPlan, planSentence, memberText, CATEGORY_WORDS, NO_GROUPS_TEXT
} from '../utils/contractProductGroups';
import './ContractProductGroupPicker.css';

/** Why the step cannot be used, or null. */
export function pickerBlock(info, categoryId) {
  if (!info) return 'Loading product groups…';
  if (!info.groups_ready) return info.reason || NO_GROUPS_TEXT;
  if (!info.total) return NO_GROUPS_TEXT;
  if (!info.ready) return info.reason;
  if (!(info.groups || []).length) {
    return `No product group holds a plain ${CATEGORY_WORDS[categoryId] || ''} product — ${info.total} group${info.total === 1 ? '' : 's'}, none for this tab`;
  }
  return null;
}

function ContractProductGroupPicker({ info, categoryId, onContract = [], disabled = false, request = null, defaultPrice = '', onAdd }) {
  const [groupId, setGroupId] = useState('');
  const [pack, setPack] = useState(null);
  const [price, setPrice] = useState('');
  const groups = useMemo(() => (info && info.groups) || [], [info]);
  const group = groups.find(g => String(g.id) === String(groupId)) || null;
  const plan = useMemo(
    () => (group ? groupAddPlan(group, { categoryId, onContract, pack }) : null),
    [group, categoryId, onContract, pack]
  );
  useSlideOutButton([groupId, plan && plan.add.length, disabled]);

  // The window's "Add n new products from the group" opens this on that group.
  // It starts at the price the contract's products share, if they share one.
  useEffect(() => {
    if (request && request.groupId) { setGroupId(String(request.groupId)); setPack(null); setPrice(defaultPrice || ''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const block = pickerBlock(info, categoryId);
  const off = disabled || !!block;
  const options = groups.map(g => ({
    value: String(g.id),
    label: g.name,
    searchText: `${g.name} ${(g.suppliers || []).join(' ')} ${g.members.map(m => `${m.item_code} ${m.item_name}`).join(' ')}`,
    renderLabel: (
      <span className="cpg-option">
        <span className="cpg-option-name">{g.name}</span>
        <span className="cpg-option-meta">
          {g.plain_count} plain product{g.plain_count === 1 ? '' : 's'}
          {(g.packs || []).length === 1 ? ` ${g.packs[0].words}` : (g.packs || []).length > 1 ? ` in ${g.packs.length} packs` : ''}
          {(g.suppliers || []).length ? ` · ${g.suppliers.join(', ')}` : ''}
        </span>
      </span>
    )
  }));

  const clear = () => { setGroupId(''); setPack(null); setPrice(''); };
  const add = () => {
    if (!plan || plan.refused || plan.needsPack || !plan.add.length) return;
    onAdd({ group, members: plan.add, price: price.trim(), pack: plan.pack });
    clear();
  };

  return (
    <div className={`cpg ${off ? 'is-off' : ''}`}>
      <div className="cpg-head">
        <span className="material-symbols-rounded cpg-icon" aria-hidden="true">category</span>
        <span className="cpg-label">Add a product group</span>
        <div className="cpg-pick" title={block || (disabled ? 'Edit the contract to add a product group' : '')}>
          <SearchableDropdown
            value={groupId}
            onChange={(v) => { setGroupId(v); setPack(null); }}
            options={options}
            disabled={off}
            placeholder={block ? (info ? 'No group to choose' : 'Loading…') : 'Choose a product group…'}
            searchPlaceholder="Search groups, products or suppliers..."
          />
        </div>
      </div>
      <p className="cpg-hint">
        {block
          ? block
          : 'Adds every plain product of the group (no additive versions) in one pack, each with its own supplier and cost — orders for any of them then count against this contract.'}
      </p>
      {group && plan && !off && (
        <div className="cpg-plan">
          {plan.needsPack && (
            <div className="cpg-packs" role="radiogroup" aria-label="Which pack">
              {plan.packs.map(p => (
                <button key={p.pack} type="button" role="radio" aria-checked={pack === p.pack}
                  className={`cpg-chip ${pack === p.pack ? 'on' : ''}`} onClick={() => setPack(p.pack)}>
                  {p.words} ({p.count})
                </button>
              ))}
            </div>
          )}
          <p className={`cpg-sentence ${plan.refused ? 'is-refused' : ''}`}>{planSentence(plan, group, { names: false })}</p>
          {plan.add.length > 0 && (
            <ul className="cpg-list">
              {plan.add.map(m => (
                <li key={m.id}>
                  <span className="material-symbols-rounded" aria-hidden="true">add_circle</span>
                  <span>{memberText(m)}</span>
                  {m.cost !== null && m.cost !== undefined && <span className="cpg-cost">cost {formatPrice(m.cost)}</span>}
                </li>
              ))}
            </ul>
          )}
          {(plan.already.length > 0 || plan.leftOut.length > 0) && (
            <ul className="cpg-list cpg-left-out">
              {plan.already.map(m => (
                <li key={`a${m.id}`}>
                  <span className="material-symbols-rounded" aria-hidden="true">check_circle</span>
                  <span>{memberText(m)} — on this contract already</span>
                </li>
              ))}
              {plan.leftOut.map(({ member: m, why }) => (
                <li key={`o${m.id}`}>
                  <span className="material-symbols-rounded" aria-hidden="true">remove_circle</span>
                  <span>{memberText(m)} — left out: {why}</span>
                </li>
              ))}
            </ul>
          )}
          {!plan.refused && !plan.needsPack && plan.add.length > 0 && (
            <div className="cpg-actions">
              <div className="form-group cpg-price">
                <label htmlFor="cpg-price">Price for each (£)</label>
                <Input id="cpg-price" type="number" step="0.01" value={price} placeholder="Leave blank to price each"
                  onChange={(e) => setPrice(e.target.value)} />
              </div>
              <span className="cpg-price-note">Typed once for all {plan.add.length} — change any product's price afterwards.</span>
              <div className="buttons slideout cpg-buttons">
                <div>
                  <button type="button" className="btn-slideout btn-slideout-primary" onClick={add}>
                    <div className="icon-wrapper"><span className="material-symbols-rounded">playlist_add</span></div>
                    <span className="btn-label">Add {plan.add.length} product{plan.add.length === 1 ? '' : 's'}</span>
                  </button>
                </div>
                <div>
                  <button type="button" className="btn-slideout" data-read-safe onClick={clear}>
                    <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                    <span className="btn-label">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ContractProductGroupPicker;
